/**
 * src/components/barbers.jsx — FINAL ESDRAS BARBERS MARKETPLACE
 * PRODUCTION BUILD 2026
 * FEATURES: Secure Cloud Key Fetching, Geospatial Discovery, Specialty Filtering
 */

import React, { useState, useEffect } from 'react';
import { GoogleMap, LoadScript, Marker, InfoWindow } from '@react-google-maps/api';
import { collection, onSnapshot, query, orderBy } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, auth, functions } from '../firebase';
import { useNavigate } from 'react-router-dom';

const NAVY = '#001F3F';
const GOLD = '#B8860B';

const mapContainerStyle = {
  width: '100%',
  height: '70vh',
  borderRadius: '28px',
  border: `5px solid ${GOLD}`,
  overflow: 'hidden',
  boxShadow: '0 10px 30px rgba(0,0,0,0.4)',
};

const defaultCenter = { lat: 6.5244, lng: 3.3792 }; // Lagos, Nigeria

export default function Barbers() {
  const [barbers, setBarbers] = useState([]);
  const [filteredBarbers, setFilteredBarbers] = useState([]);
  const [selected, setSelected] = useState(null);
  const [userLocation, setUserLocation] = useState(null);
  const [map, setMap] = useState(null);
  const [specialtyFilter, setSpecialtyFilter] = useState('all'); // all, men, women, both
  const [mapsKey, setMapsKey] = useState(null);

  const navigate = useNavigate();

  // 1. Securely fetch the Google Maps API Key from Cloud Functions
  // This prevents the API key from being exposed in the client-side build files
  useEffect(() => {
    const fetchKey = async () => {
      try {
        const getMapsKey = httpsCallable(functions, 'getMapsKey');
        const result = await getMapsKey();
        setMapsKey(result.data.key);
      } catch (error) {
        console.error("Failed to secure Google Maps Key:", error);
      }
    };
    fetchKey();
  }, []);

  // 2. Handle User Location and Stylist Data Fetching
  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const loc = {
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          };
          setUserLocation(loc);
          if (map) map.panTo(loc);
        },
        (error) => {
          console.log('Location access denied or unavailable:', error);
        },
        { timeout: 10000 }
      );
    }

    // Real-time snapshot of top-rated stylists
    const q = query(
      collection(db, 'barbers'),
      orderBy('averageRating', 'desc')
    );

    const unsub = onSnapshot(
      q,
      (snap) => {
        const data = snap.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
          monnifySubAccountCode: doc.data().monnifySubAccountCode || null 
        }));
        setBarbers(data);
      },
      (err) => {
        console.error('Failed to load stylists:', err);
      }
    );

    return () => unsub();
  }, [map]);

  // 3. Apply specialty filter logic whenever the list or filter changes
  useEffect(() => {
    if (specialtyFilter === 'all') {
      setFilteredBarbers(barbers);
    } else {
      setFilteredBarbers(
        barbers.filter((b) => {
          const specialty = (b.specialty || '').toLowerCase();
          if (specialtyFilter === 'women') return specialty.includes('women') || specialty === 'both';
          if (specialtyFilter === 'both') return specialty === 'both';
          return specialty === specialtyFilter;
        })
      );
    }
  }, [barbers, specialtyFilter]);

  const handleBooking = (barber) => {
    if (!auth.currentUser) {
      alert('Please log in to book a stylist');
      navigate('/login');
      return;
    }
    navigate(`/barber-detail/${barber.id}`);
  };

  // Prevent map from rendering until the secure key is available
  if (!mapsKey) {
    return (
      <div style={{ minHeight: '100vh', background: NAVY, display: 'grid', placeItems: 'center' }}>
        <h2 style={{ color: GOLD, fontFamily: 'Montserrat', letterSpacing: '2px' }}>
          INITIALIZING SECURE MAP...
        </h2>
      </div>
    );
  }

  return (
    <div
      style={{
        minHeight: '100vh',
        background: NAVY,
        color: 'white',
        fontFamily: 'Montserrat, sans-serif',
        padding: '2rem 1rem',
        textAlign: 'center',
      }}
    >
      {/* Logo */}
      <div style={{ margin: '2rem 0' }}>
        <img
          src="/icons/esdras-logo.png"
          alt="Esdras Logo"
          style={{ width: 160, height: 160, objectFit: 'contain' }}
        />
      </div>

      <h1 style={{ fontSize: '3rem', fontWeight: '800', color: GOLD, margin: '0' }}>esdras</h1>
      <h2
        style={{
          textAlign: 'center',
          color: GOLD,
          fontSize: '2.5rem',
          fontWeight: '800',
          margin: '0.5rem 0 1.5rem',
        }}
      >
        Top Rated Stylists
      </h2>

      <p style={{ fontSize: '1.4rem', opacity: 0.9, marginBottom: '2rem' }}>
        {userLocation
          ? 'Best stylists near you in Lagos'
          : 'Allow location access for personalized recommendations'}
      </p>

      {/* Specialty Filter Buttons */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          gap: '1rem',
          margin: '2rem 0',
          flexWrap: 'wrap',
        }}
      >
        {['all', 'men', 'women', 'both'].map((filter) => (
          <button
            key={filter}
            onClick={() => setSpecialtyFilter(filter)}
            style={{
              background: specialtyFilter === filter ? GOLD : 'rgba(255,255,255,0.1)',
              color: specialtyFilter === filter ? 'black' : 'white',
              padding: '1rem 2rem',
              borderRadius: '50px',            
              fontWeight: 'bold',
              border: 'none',
              cursor: 'pointer',
              minWidth: '120px',
              transition: 'all 0.3s',
            }}
          >
            {filter.charAt(0).toUpperCase() + filter.slice(1)}
          </button>
        ))}

        <button 
          onClick={() => navigate('/gallery')}
          style={{
            background: 'rgba(184, 134, 11, 0.2)',
            color: GOLD,
            padding: '1rem 2rem',
            borderRadius: '50px',
            fontWeight: 'bold',
            border: `2px solid ${GOLD}`,
            cursor: 'pointer',
            minWidth: '150px'
          }}
        >
          🌍 VIEW STYLE VAULT
        </button>
      </div>

      {/* Google Map Interface */}
      <LoadScript googleMapsApiKey={mapsKey}>
        <GoogleMap
          mapContainerStyle={mapContainerStyle}
          center={userLocation || defaultCenter}
          zoom={userLocation ? 14 : 11}
          onLoad={setMap}
          options={{
            styles: [
              { featureType: 'poi', elementType: 'labels', stylers: [{ visibility: 'off' }] },
            ],
            disableDefaultUI: false,
            zoomControl: true,
            mapTypeControl: false,
            streetViewControl: false,
            fullscreenControl: false,
          }}
        >
          {filteredBarbers.map((barber) => {
            if (!barber.lat || !barber.lng) return null;
            return (
              <Marker
                key={barber.id}
                position={{ lat: barber.lat, lng: barber.lng }}
                onClick={() => setSelected(barber)}
                icon={{
                  url: '/gold-target-icon.png',
                  scaledSize: new window.google.maps.Size(56, 56),
                  origin: new window.google.maps.Point(0, 0),
                  anchor: new window.google.maps.Point(28, 28),
                }}
              />
            );
          })}

          {selected && selected.lat && selected.lng && (
            <InfoWindow
              position={{ lat: selected.lat, lng: selected.lng }}
              onCloseClick={() => setSelected(null)}
            >
              <div
                style={{
                  padding: '1rem',
                  maxWidth: '280px',
                  fontFamily: 'Montserrat, sans-serif',
                  color: '#000',
                }}
              >
                <h3 style={{ margin: '0 0 0.5rem', fontSize: '1.4rem', color: NAVY }}>
                  {selected.shopName || selected.name || 'Stylist'}
                </h3>

                {selected.specialty &&
                  (selected.specialty.toLowerCase().includes('women') ||
                    selected.specialty === 'both') && (
                    <p style={{ color: GOLD, fontWeight: 'bold', margin: '0.5rem 0' }}>
                      ✨ Women Specialist
                    </p>
                  )}

                <p style={{ margin: '0.4rem 0', opacity: 0.8 }}>
                  ★ {selected.averageRating?.toFixed(1) || '5.0'} • {selected.area || 'Lagos'}
                </p>

                <p style={{ margin: '0.6rem 0', color: GOLD, fontWeight: 'bold' }}>
                  {selected.specialty || 'Precision Cuts & Styling'}
                </p>

                <p style={{ margin: '0.6rem 0', fontSize: '1.1rem' }}>
                  ₦{(selected.priceLow || 3000).toLocaleString()} – ₦
                  {(selected.priceHigh || 8000).toLocaleString()}
                </p>

                <button
                  onClick={() => handleBooking(selected)}
                  style={{
                    width: '100%',
                    background: GOLD,
                    color: 'black',                    
                    border: 'none',
                    padding: '1rem',
                    borderRadius: '50px',
                    fontWeight: 'bold',
                    fontSize: '1.2rem',
                    cursor: 'pointer',
                    marginTop: '0.8rem',
                  }}
                >
                  Book Now • 10% Esdras Fee
                </button>
              </div>
            </InfoWindow>
          )}
        </GoogleMap>
      </LoadScript>

      <p style={{ marginTop: '3rem', opacity: 0.7, fontSize: '1rem', lineHeight: '1.6' }}>
        Powered by Esdras • Lagos’ Precision Grooming Marketplace<br /><br />
        We only take 10% — your stylist gets the rest
      </p>
    </div>
  );
}
